import { GripVertical, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { AppLayout } from "@/components/layout/app-layout";
import { type Exercise, type FacilitatorChecklistItem, type Module, MODULES } from "@/data/modules";
import {
  type AgendaRow,
  agendaStorageKey,
  minutesToTime,
  parseDurationToMinutes,
  parseTimeStr,
  seedAgendaRows,
} from "@/lib/agenda";
import { useFacilitatorState, useFacilitatorStateWriter } from "@/lib/api";

import { FacilitatorSaveStatus } from "./save-status";
import { moveRowById, useAgendaDnd } from "./use-agenda-dnd";

// ---------------------------------------------------------------------------
// Prepare tab
// ---------------------------------------------------------------------------

interface PrepItem {
  id: string;
  text: string;
  checked: boolean;
  owner: string;
}

interface PrepCategory {
  id: string;
  category: string;
  items: PrepItem[];
}

function prepListStorageKey(moduleId: number): string {
  return `fac-prep-list-${moduleId}`;
}

/** Build the editable checklist from module data, migrating any previously
 *  ticked boxes stored under the old per-index keys so nothing is lost. */
function seedPrep(checklist: FacilitatorChecklistItem[]): PrepCategory[] {
  return checklist.map((cat) => ({
    id: crypto.randomUUID(),
    category: cat.category,
    items: cat.items.map((raw) => {
      const item = typeof raw === "string" ? { text: raw } : raw;
      return {
        id: crypto.randomUUID(),
        text: item.text,
        checked: item.checked ?? false,
        owner: item.owner ?? "",
      };
    }),
  }));
}

interface PrepTabProps {
  moduleId: number;
  checklist: FacilitatorChecklistItem[];
  slideDecks: { label: string; url: string }[];
}

function PrepTab({ moduleId, checklist, slideDecks }: PrepTabProps) {
  const { data: state } = useFacilitatorState();
  const { setKey } = useFacilitatorStateWriter();
  const key = prepListStorageKey(moduleId);
  const seed = useMemo(() => seedPrep(checklist), [checklist]);
  const cats = (state?.[key] as PrepCategory[] | undefined) ?? seed;

  const save = (next: PrepCategory[]): void => setKey(key, next);

  const mapItems = (catId: string, fn: (items: PrepItem[]) => PrepItem[]): void =>
    save(cats.map((c) => (c.id === catId ? { ...c, items: fn(c.items) } : c)));

  const toggleItem = (catId: string, itemId: string): void =>
    mapItems(catId, (items) =>
      items.map((it) => (it.id === itemId ? { ...it, checked: !it.checked } : it)),
    );

  const editItem = (catId: string, itemId: string, text: string): void =>
    mapItems(catId, (items) => items.map((it) => (it.id === itemId ? { ...it, text } : it)));

  const deleteItem = (catId: string, itemId: string): void =>
    mapItems(catId, (items) => items.filter((it) => it.id !== itemId));

  const addItem = (catId: string): void =>
    mapItems(catId, (items) => [
      ...items,
      { id: crypto.randomUUID(), text: "", checked: false, owner: "" },
    ]);

  const setOwner = (catId: string, itemId: string, owner: string): void =>
    mapItems(catId, (items) => items.map((it) => (it.id === itemId ? { ...it, owner } : it)));

  const totalItems = cats.reduce((sum, cat) => sum + cat.items.length, 0);
  const totalTicked = cats.reduce(
    (sum, cat) => sum + cat.items.filter((it) => it.checked).length,
    0,
  );
  const pct = totalItems > 0 ? Math.round((totalTicked / totalItems) * 100) : 0;

  return (
    <div>
      {/* Overall progress bar */}
      <div className="mb-6">
        <div className="mb-1 flex items-center justify-between text-sm">
          <span className="text-muted font-medium">Overall progress</span>
          <span className="text-muted">
            {totalTicked} / {totalItems}
          </span>
        </div>
        <div className="bg-muted/20 h-2 w-full overflow-hidden rounded-full">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Checklist categories */}
      <div className="space-y-6">
        {cats.map((cat) => {
          const catTicked = cat.items.filter((it) => it.checked).length;
          const catTotal = cat.items.length;

          return (
            <div key={cat.id} className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-foreground text-sm font-semibold">{cat.category}</h3>
                <span className="text-muted text-xs">
                  {catTicked} / {catTotal}
                </span>
              </div>

              <ul className="space-y-2">
                {cat.items.map((item) => (
                  <li key={item.id} className="group flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={item.checked}
                      onChange={() => toggleItem(cat.id, item.id)}
                      className="border-border mt-1 h-4 w-4 flex-shrink-0 cursor-pointer accent-[var(--color-primary)]"
                    />
                    <input
                      value={item.text}
                      onChange={(e) => editItem(cat.id, item.id, e.target.value)}
                      placeholder="New checklist item"
                      className={`min-w-0 flex-1 border-none bg-transparent text-sm leading-snug outline-none placeholder:text-gray-300 ${
                        item.checked ? "text-muted line-through" : "text-foreground"
                      }`}
                    />
                    <OwnerSelect
                      value={item.owner}
                      onChange={(v) => setOwner(cat.id, item.id, v)}
                      colorize
                    />
                    <button
                      type="button"
                      onClick={() => deleteItem(cat.id, item.id)}
                      title="Delete item"
                      className="mt-0.5 flex-shrink-0 text-sm leading-none text-gray-300 opacity-0 transition-opacity group-hover:opacity-100 hover:text-red-500"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={() => addItem(cat.id)}
                className="text-primary hover:text-primary/80 mt-3 text-xs font-medium"
              >
                + Add item
              </button>
            </div>
          );
        })}
      </div>

      {/* Last year's slide decks */}
      {slideDecks.length > 0 && (
        <div className="mt-8">
          <h3 className="text-foreground mb-4 text-base font-semibold">Last year's slide decks</h3>
          <div className="space-y-3">
            {slideDecks.map((deck, idx) => (
              <div
                key={idx}
                className="border-border flex items-center justify-between gap-4 rounded-xl border bg-white p-4"
              >
                <p className="text-foreground text-sm font-medium">{deck.label}</p>
                <a
                  href={deck.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary flex-shrink-0 text-sm font-medium hover:underline"
                >
                  Open deck →
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Run tab
// ---------------------------------------------------------------------------

function ExerciseCard({ exercise }: { exercise: Exercise }) {
  return (
    <div className="border-border rounded-lg border bg-white p-4">
      <p className="text-foreground text-sm font-semibold">{exercise.title}</p>

      <div className="mt-1.5 flex flex-wrap gap-2">
        {exercise.timing && (
          <span className="text-muted rounded-full bg-gray-100 px-2.5 py-0.5 text-xs">
            {exercise.timing}
          </span>
        )}
        {exercise.format && (
          <span className="text-muted rounded-full bg-gray-100 px-2.5 py-0.5 text-xs">
            {exercise.format}
          </span>
        )}
      </div>

      {exercise.facilitatorNotes && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="mb-1 text-xs font-semibold text-amber-900">Facilitator notes</p>
          <p className="text-xs leading-relaxed text-amber-900">{exercise.facilitatorNotes}</p>
        </div>
      )}
    </div>
  );
}

interface RunTabProps {
  sessions: import("@/data/modules").Session[];
  slideUrls: { day: number; label: string; url: string }[];
}

function RunTab({ sessions, slideUrls }: RunTabProps) {
  return (
    <div className="space-y-8">
      {sessions.map((session) => {
        const slide = slideUrls.find((s) => s.day === session.day);

        // Collect exercises: from sections or from session.exercises
        const exercises: Exercise[] = [];
        if (session.sections) {
          for (const section of session.sections) {
            exercises.push(...section.exercises);
          }
        }
        if (session.exercises) {
          exercises.push(...session.exercises);
        }
        // Filter out empty sections (e.g. lunch breaks with no exercises)
        const nonEmptyExercises = exercises.filter((ex) => ex.title);

        return (
          <div key={session.day} className="rounded-2xl border border-gray-100 bg-gray-50 p-6">
            {/* Card header */}
            <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-muted mb-0.5 text-xs font-semibold tracking-wider uppercase">
                  Day {session.day}
                </p>
                <h3 className="text-foreground text-base leading-snug font-bold">
                  {session.title}
                </h3>
                {session.timeRange && (
                  <p className="text-muted mt-0.5 text-sm">{session.timeRange}</p>
                )}
              </div>

              {/* Slide deck button */}
              {slide ? (
                <a
                  href={slide.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-primary text-primary hover:bg-primary/5 inline-flex items-center gap-1.5 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
                >
                  Open slide deck →
                </a>
              ) : (
                <button
                  type="button"
                  disabled
                  className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-400"
                >
                  Add slide URL
                </button>
              )}
            </div>

            {/* Exercises */}
            {nonEmptyExercises.length > 0 && (
              <div className="space-y-3">
                {nonEmptyExercises.map((ex, idx) => (
                  <ExerciseCard key={idx} exercise={ex} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agenda tab (spreadsheet-style editable table)
// ---------------------------------------------------------------------------

interface AgendaTabProps {
  module: Module;
}

// ---------------------------------------------------------------------------
// Owner dropdown — Pieter / Sofie / All (+ blank). Any legacy free-text owner
// is preserved as an extra option so no previously entered value is lost.
// ---------------------------------------------------------------------------

const AGENDA_OWNERS = ["Pieter", "Sofie", "Dome", "All"] as const;

// Distinctive colour per owner. Used for the checklist owner tags and the
// agenda row tint (Pieter, Sofie, and Dome rows are tinted; All is not).
const OWNER_TAG_COLORS: Record<string, string> = {
  Pieter: "border-blue-200 bg-blue-100 text-blue-700",
  Sofie: "border-rose-200 bg-rose-100 text-rose-700",
  Dome: "border-violet-200 bg-violet-100 text-violet-700",
  All: "border-emerald-200 bg-emerald-100 text-emerald-700",
};

function ownerRowTint(owner: string): string {
  switch (owner) {
    case "Pieter":
      return "bg-blue-50 hover:bg-blue-100";
    case "Sofie":
      return "bg-rose-50 hover:bg-rose-100";
    case "Dome":
      return "bg-violet-50 hover:bg-violet-100";
    default:
      return "bg-white hover:bg-gray-50";
  }
}

function OwnerSelect({
  value,
  onChange,
  className,
  colorize,
}: {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  colorize?: boolean;
}) {
  const isLegacy = value !== "" && !AGENDA_OWNERS.includes(value as (typeof AGENDA_OWNERS)[number]);
  const tagColor = OWNER_TAG_COLORS[value] ?? "border-gray-200 bg-white text-muted";
  const resolvedClassName = colorize
    ? `mt-0.5 flex-shrink-0 cursor-pointer rounded-md border px-1.5 py-0.5 text-xs outline-none ${tagColor}`
    : (className ??
      "text-foreground w-full cursor-pointer border-none bg-transparent text-sm outline-none");
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={resolvedClassName}
      title="Owner"
    >
      <option value="">—</option>
      {isLegacy && <option value={value}>{value}</option>}
      {AGENDA_OWNERS.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

function AgendaTab({ module }: AgendaTabProps) {
  const storageKey = agendaStorageKey(module.id);
  const { data: state } = useFacilitatorState();
  const { setKey } = useFacilitatorStateWriter();
  const seed = useMemo(() => seedAgendaRows(module), [module]);
  const rows = (state?.[storageKey] as AgendaRow[] | undefined) ?? seed;

  const saveRows = (next: AgendaRow[]): void => setKey(storageKey, next);

  const { handleProps, rowProps, indicator } = useAgendaDnd((fromId, targetId, pos) =>
    saveRows(moveRowById(rows, fromId, targetId, pos)),
  );

  const updateRow = (id: string, field: keyof AgendaRow, value: string): void => {
    saveRows(rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const deleteRow = (id: string): void => {
    saveRows(rows.filter((r) => r.id !== id));
  };

  const addRowBelow = (id: string): void => {
    const idx = rows.findIndex((r) => r.id === id);
    const ref = rows[idx]!;
    const newRow: AgendaRow = {
      id: crypto.randomUUID(),
      type: "activity",
      day: ref.day,
      number: "",
      name: "",
      duration: "",
      owner: "",
      comment: "",
    };
    const next = [...rows];
    next.splice(idx + 1, 0, newRow);
    saveRows(next);
  };

  const addDay = (): void => {
    const maxDay = rows.reduce((m, r) => Math.max(m, r.day), 0);
    const newDay = maxDay + 1;
    const dayRow: AgendaRow = {
      id: crypto.randomUUID(),
      type: "day",
      day: newDay,
      number: "",
      name: `DAY ${newDay}`,
      duration: "",
      owner: "",
      startTime: "09:00",
    };
    const actRow: AgendaRow = {
      id: crypto.randomUUID(),
      type: "activity",
      day: newDay,
      number: "",
      name: "",
      duration: "",
      owner: "",
      comment: "",
    };
    saveRows([...rows, dayRow, actRow]);
  };

  /** Derive start + end time for every row based on cumulative durations */
  const computedTimes = useMemo(() => {
    const result: Record<string, { start: string; end: string }> = {};
    let cursor = 9 * 60; // default day start (minutes since midnight)

    for (const row of rows) {
      if (row.type === "day") {
        cursor = parseTimeStr(row.startTime ?? "09:00");
        result[row.id] = { start: row.startTime ?? "09:00", end: "" };
      } else {
        const startMins = cursor;
        const durationMins = parseDurationToMinutes(row.duration);
        const endMins = startMins + durationMins;
        result[row.id] = {
          start: minutesToTime(startMins),
          end: durationMins > 0 ? minutesToTime(endMins) : "",
        };
        if (durationMins > 0) {
          cursor = endMins;
        }
      }
    }

    return result;
  }, [rows]);

  const inputCls = "bg-transparent border-none outline-none w-full text-sm";
  // Duration cells edit the number of minutes only; the "min" unit is a
  // fixed label beside the field so it can never be typed into or mangled.
  const durationInputCls =
    "bg-transparent border-none outline-none text-sm w-12 text-right tabular-nums";
  const durationMinsValue = (d: string): string => {
    const mins = parseDurationToMinutes(d);
    return mins > 0 ? String(mins) : "";
  };

  /** Border showing where a dragged row will land */
  const dropCls = (id: string): string => {
    const ind = indicator(id);
    if (ind === "before") {
      return "border-t-2 border-t-primary";
    }
    if (ind === "after") {
      return "border-b-2 border-b-primary";
    }
    return "";
  };

  return (
    <div>
      <p className="text-muted mb-3 text-xs">
        Drag the handle on the left of any row to reorder the agenda.
      </p>
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="w-[28px] px-1 py-2" />
              <th className="text-muted w-[50px] px-3 py-2 text-left text-xs font-semibold tracking-wide uppercase">
                #
              </th>
              <th className="text-muted px-3 py-2 text-left text-xs font-semibold tracking-wide uppercase">
                Activity
              </th>
              <th className="text-muted w-[80px] px-3 py-2 text-left text-xs font-semibold tracking-wide uppercase">
                Start
              </th>
              <th className="text-muted w-[80px] px-3 py-2 text-left text-xs font-semibold tracking-wide uppercase">
                End
              </th>
              <th className="text-muted w-[90px] px-3 py-2 text-left text-xs font-semibold tracking-wide uppercase">
                Duration
              </th>
              <th className="text-muted w-[120px] px-3 py-2 text-left text-xs font-semibold tracking-wide uppercase">
                Owner
              </th>
              <th className="text-muted w-[180px] px-3 py-2 text-left text-xs font-semibold tracking-wide uppercase">
                Comments
              </th>
              <th className="w-[60px] px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              if (row.type === "day") {
                return (
                  <tr
                    key={row.id}
                    {...rowProps(row.id)}
                    className={`bg-[#FF00FF] font-bold text-white ${dropCls(row.id)}`}
                  >
                    <td className="px-1 py-2 text-center">
                      <span
                        {...handleProps(row.id)}
                        title="Drag to reorder"
                        className="inline-flex cursor-grab text-white/60 hover:text-white active:cursor-grabbing"
                      >
                        <GripVertical className="h-3.5 w-3.5" />
                      </span>
                    </td>
                    <td />
                    <td className="px-3 py-2">
                      <input
                        className={`${inputCls} font-bold text-white placeholder:text-white/60`}
                        value={row.name}
                        onChange={(e) => updateRow(row.id, "name", e.target.value)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        className={`${inputCls} font-bold text-white placeholder:text-white/60`}
                        value={row.startTime ?? "09:00"}
                        onChange={(e) => updateRow(row.id, "startTime", e.target.value)}
                        placeholder="09:00"
                        title="Day start time"
                      />
                    </td>
                    <td colSpan={3} />
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => addRowBelow(row.id)}
                        title="Add row below"
                        className="rounded bg-white/20 px-1.5 py-0.5 text-xs text-white hover:bg-white/30"
                      >
                        +
                      </button>
                    </td>
                  </tr>
                );
              }

              if (row.type === "lunch") {
                const times = computedTimes[row.id];
                return (
                  <tr
                    key={row.id}
                    {...rowProps(row.id)}
                    className={`bg-gray-700 font-semibold text-white ${dropCls(row.id)}`}
                  >
                    <td className="px-1 py-2 text-center">
                      <span
                        {...handleProps(row.id)}
                        title="Drag to reorder"
                        className="inline-flex cursor-grab text-white/60 hover:text-white active:cursor-grabbing"
                      >
                        <GripVertical className="h-3.5 w-3.5" />
                      </span>
                    </td>
                    <td />
                    <td className="px-3 py-2">
                      <input
                        className={`${inputCls} font-semibold text-white placeholder:text-white/60`}
                        value={row.name}
                        onChange={(e) => updateRow(row.id, "name", e.target.value)}
                      />
                    </td>
                    <td className="px-3 py-2 text-xs text-white/80">{times?.start ?? ""}</td>
                    <td className="px-3 py-2 text-xs text-white/80">{times?.end ?? ""}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <input
                          className={`${durationInputCls} text-white placeholder:text-white/60`}
                          inputMode="numeric"
                          value={durationMinsValue(row.duration)}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, "");
                            updateRow(row.id, "duration", digits ? `${digits} min` : "");
                          }}
                          placeholder="30"
                        />
                        <span className="text-xs text-white/60">min</span>
                      </div>
                    </td>
                    <td />
                    <td className="px-3 py-2">
                      <input
                        className={`${inputCls} text-white placeholder:text-white/60`}
                        value={row.comment ?? ""}
                        onChange={(e) => updateRow(row.id, "comment", e.target.value)}
                        placeholder="Comment…"
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => addRowBelow(row.id)}
                          title="Add row below"
                          className="rounded bg-white/20 px-1.5 py-0.5 text-xs text-white hover:bg-white/30"
                        >
                          +
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteRow(row.id)}
                          title="Delete row"
                          className="rounded bg-white/20 px-1.5 py-0.5 text-xs text-white hover:bg-red-500/60"
                        >
                          ×
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              }

              if (row.type === "note") {
                return (
                  <tr
                    key={row.id}
                    {...rowProps(row.id)}
                    className={`border-b border-gray-100 bg-amber-50 ${dropCls(row.id)}`}
                  >
                    <td className="px-1 py-2 text-center">
                      <span
                        {...handleProps(row.id)}
                        title="Drag to reorder"
                        className="inline-flex cursor-grab text-gray-300 hover:text-gray-500 active:cursor-grabbing"
                      >
                        <GripVertical className="h-3.5 w-3.5" />
                      </span>
                    </td>
                    <td className="px-3 py-2" />
                    <td className="px-3 py-2" colSpan={4}>
                      <input
                        className={inputCls}
                        value={row.name}
                        onChange={(e) => updateRow(row.id, "name", e.target.value)}
                        placeholder="Note…"
                      />
                    </td>
                    <td />
                    <td className="px-3 py-2">
                      <input
                        className={inputCls}
                        value={row.comment ?? ""}
                        onChange={(e) => updateRow(row.id, "comment", e.target.value)}
                        placeholder="Comment…"
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => addRowBelow(row.id)}
                          title="Add row below"
                          className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-200"
                        >
                          +
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteRow(row.id)}
                          title="Delete row"
                          className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 hover:bg-red-100 hover:text-red-600"
                        >
                          ×
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              }

              // activity
              const times = computedTimes[row.id];
              return (
                <tr
                  key={row.id}
                  {...rowProps(row.id)}
                  className={`border-b border-gray-100 ${ownerRowTint(row.owner)} ${dropCls(row.id)}`}
                >
                  <td className="px-1 py-2 text-center">
                    <span
                      {...handleProps(row.id)}
                      title="Drag to reorder"
                      className="inline-flex cursor-grab text-gray-300 hover:text-gray-500 active:cursor-grabbing"
                    >
                      <GripVertical className="h-3.5 w-3.5" />
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      className={inputCls}
                      value={row.number}
                      onChange={(e) => updateRow(row.id, "number", e.target.value)}
                      placeholder="—"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      className={inputCls}
                      value={row.name}
                      onChange={(e) => updateRow(row.id, "name", e.target.value)}
                      placeholder="Activity name"
                    />
                  </td>
                  <td className="text-muted px-3 py-2 text-xs tabular-nums">
                    {times?.start ?? ""}
                  </td>
                  <td className="text-muted px-3 py-2 text-xs tabular-nums">{times?.end ?? ""}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <input
                        className={durationInputCls}
                        inputMode="numeric"
                        value={durationMinsValue(row.duration)}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, "");
                          updateRow(row.id, "duration", digits ? `${digits} min` : "");
                        }}
                        placeholder="30"
                      />
                      <span className="text-muted text-xs">min</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <OwnerSelect
                      value={row.owner}
                      onChange={(v) => updateRow(row.id, "owner", v)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      className={inputCls}
                      value={row.comment ?? ""}
                      onChange={(e) => updateRow(row.id, "comment", e.target.value)}
                      placeholder="Comment…"
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        onClick={() => addRowBelow(row.id)}
                        title="Add row below"
                        className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-200"
                      >
                        +
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteRow(row.id)}
                        title="Delete row"
                        className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 hover:bg-red-100 hover:text-red-600"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add day button */}
      <button
        type="button"
        onClick={addDay}
        className="text-primary border-primary hover:bg-primary/5 mt-4 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
      >
        + Add day
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Materials tab
// ---------------------------------------------------------------------------

interface MaterialsTabProps {
  prints: import("@/data/modules").Print[];
  preparation: string[];
}

function MaterialsTab({ prints, preparation }: MaterialsTabProps) {
  return (
    <div className="space-y-8">
      {/* Print list */}
      <div>
        <h3 className="text-foreground mb-4 text-base font-semibold">Print list</h3>
        {prints.length === 0 ? (
          <p className="text-muted text-sm">No prints for this module.</p>
        ) : (
          <div className="space-y-3">
            {prints.map((print, idx) => (
              <div
                key={idx}
                className="border-border flex items-start justify-between gap-4 rounded-xl border bg-white p-4"
              >
                <div>
                  <p className="text-foreground text-sm font-medium">{print.title}</p>
                  <p className="text-muted mt-0.5 text-sm leading-snug">{print.description}</p>
                </div>
                {print.url && (
                  <a
                    href={print.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary flex-shrink-0 text-sm font-medium hover:underline"
                  >
                    Download →
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Entry ticket */}
      <div>
        <h3 className="text-foreground mb-4 text-base font-semibold">Entry ticket</h3>
        {preparation.length === 0 ? (
          <p className="text-muted text-sm">No entry requirements for this module.</p>
        ) : (
          <ul className="space-y-1.5">
            {preparation.map((item, idx) => (
              <li key={idx} className="text-muted flex items-start gap-2 text-sm">
                <span className="text-primary mt-0.5 flex-shrink-0">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type Tab = "prepare" | "run" | "agenda" | "materials";

export function FacilitatorModulePage() {
  const { id } = useParams<{ id: string }>();
  const moduleId = id ? parseInt(id, 10) : NaN;
  const module = MODULES.find((m) => m.id === moduleId);

  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get("tab") as Tab | null) ?? "prepare";
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const { isLoading: stateLoading } = useFacilitatorState();

  if (!module) {
    return (
      <AppLayout>
        <div className="mx-auto max-w-4xl px-6 py-12">
          <p className="text-muted text-sm">Module not found.</p>
          <Link
            to="/facilitator"
            className="text-primary mt-4 inline-block text-sm font-medium hover:underline"
          >
            ← All modules
          </Link>
        </div>
      </AppLayout>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: "prepare", label: "Prepare" },
    { id: "run", label: "Run" },
    { id: "agenda", label: "Agenda" },
    { id: "materials", label: "Materials" },
  ];

  return (
    <AppLayout>
      <div className="mx-auto max-w-4xl px-6 py-10">
        {/* Back link */}
        <Link
          to="/facilitator"
          className="text-muted hover:text-foreground mb-6 inline-block text-sm font-medium transition-colors"
        >
          ← All modules
        </Link>

        {/* Page header */}
        <div className="mb-8">
          <p className="text-muted mb-1 text-xs font-semibold tracking-wider uppercase">
            Module {module.id}
          </p>
          <h1 className="font-heading text-foreground text-2xl font-bold md:text-3xl">
            {module.title}
          </h1>
          <p className="text-muted mt-1 text-sm">
            {module.location} <span className="mx-1">•</span> {module.month}
          </p>
        </div>

        {/* Tabs */}
        <div className="border-border mb-8 flex overflow-x-auto border-b whitespace-nowrap">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`mr-1 flex-shrink-0 px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "border-primary text-primary border-b-2"
                  : "text-muted hover:text-foreground"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="mb-3 flex min-h-[18px] justify-end">
          <FacilitatorSaveStatus />
        </div>
        {stateLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="text-muted h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            {activeTab === "prepare" && (
              <PrepTab
                moduleId={module.id}
                checklist={module.facilitator.checklist}
                slideDecks={module.facilitator.slideDecks}
              />
            )}
            {activeTab === "run" && (
              <RunTab sessions={module.during.sessions} slideUrls={module.facilitator.slideUrls} />
            )}
            {activeTab === "agenda" && <AgendaTab module={module} />}
            {activeTab === "materials" && (
              <MaterialsTab prints={module.during.prints} preparation={module.before.preparation} />
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
